/**
 * Runtime-policy API: the kinds of work ("surfaces"), their allowed/default policies,
 * and the "change the policy of X" calls for every entity that runs under one.
 * Everything is addressed by id.
 */
import {
  clearOrganizationRuntimePolicySurfaceRule,
  listRuntimePolicyOptions,
  listRuntimePolicySurfaceRules,
  resolveModelRuntimePolicy,
  setConversationRuntimePolicy,
  setDecisionRuntimePolicy,
  setEmployeeRuntimePolicy,
  setOrganizationRuntimePolicySurfaceRule,
  setSystemRuntimePolicySurfaceRule,
  setWorkflowRuntimePolicy,
} from '../../generated/api/sdk.gen.js';
import { RuntimePolicySurface } from '../../generated/api/types.gen.js';
import type { ModelRuntimePolicyResolveRequest, RuntimePolicySurfaceRuleWrite } from '../../generated/api/types.gen.js';
import { unwrapGenerated } from './generated.js';

/** Narrow a surface name arriving as a plain string (e.g. on a card) to the SDK enum; null when unknown. */
export function toRuntimePolicySurface(value: string): RuntimePolicySurface | null {
  return Object.values(RuntimePolicySurface).find((surface) => surface === value) ?? null;
}

export const runtimePolicyApi = {
  listSurfaceRules: () => unwrapGenerated(listRuntimePolicySurfaceRules()),
  listOptions: (surface: RuntimePolicySurface) => unwrapGenerated(listRuntimePolicyOptions({ path: { surface } })),
  setSystemRule: (surface: RuntimePolicySurface, body: RuntimePolicySurfaceRuleWrite) =>
    unwrapGenerated(setSystemRuntimePolicySurfaceRule({ path: { surface }, body })),
  setOrganizationRule: (surface: RuntimePolicySurface, body: RuntimePolicySurfaceRuleWrite) =>
    unwrapGenerated(setOrganizationRuntimePolicySurfaceRule({ path: { surface }, body })),
  clearOrganizationRule: (surface: RuntimePolicySurface) =>
    unwrapGenerated(clearOrganizationRuntimePolicySurfaceRule({ path: { surface } })),
  resolve: (body: ModelRuntimePolicyResolveRequest) => unwrapGenerated(resolveModelRuntimePolicy({ body })),

  setConversationPolicy: (conversationId: string, runtimePolicyId: number) =>
    unwrapGenerated(setConversationRuntimePolicy({ path: { id: conversationId }, body: { runtimePolicyId } })),
  setEmployeePolicy: (employeeId: number, runtimePolicyId: number) =>
    unwrapGenerated(setEmployeeRuntimePolicy({ path: { id: employeeId }, body: { runtimePolicyId } })),
  setWorkflowPolicy: (workflowId: number, runtimePolicyId: number) =>
    unwrapGenerated(setWorkflowRuntimePolicy({ path: { id: workflowId }, body: { runtimePolicyId } })),
  setDecisionPolicy: (decisionId: number, runtimePolicyId: number) =>
    unwrapGenerated(setDecisionRuntimePolicy({ path: { id: decisionId }, body: { runtimePolicyId } })),
};
